import React, { useEffect, useState } from 'react';
import { songService } from '../services/api';

const SongDetailModal = ({ slug, onClose }) => {
  const [song, setSong] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDetail = async () => {
      try {
        const data = await songService.getSongDetail(slug);
        setSong(data);
      } catch (err) {
        console.error('Failed to load song details');
      } finally {
        setLoading(false);
      }
    };
    if (slug) fetchDetail();
  }, [slug]);

  if (!slug) return null;

  return (
    <div 
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 backdrop-blur-sm animate-fade-in"
      onClick={onClose}
    >
      {/* Modal Container */}
      <div 
        className="bg-white w-full max-w-2xl sm:rounded-3xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh] sm:max-h-[85vh] animate-slide-up ring-1 ring-gray-900/5"
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header with blurred background sticky effect */}
        <div className="relative p-6 border-b border-gray-100 bg-white/80 backdrop-blur-md z-10 flex justify-between items-start shrink-0">
          <div className="pr-8">
            <h2 className="text-2xl font-bold text-gray-900 leading-tight tracking-tight">
              {song?.title || 'Loading...'}
            </h2>
            {['HWC', 'VIP', 'CP'].includes(song?.collection?.toUpperCase()) && (
              <span 
                className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-sm font-bold uppercase mt-2 border ${
                  song.collection?.toUpperCase() === 'HWC'
                    ? 'bg-[#595959] text-[#FFFFFF] border-[#595959]'
                    : song.collection?.toUpperCase() === 'VIP'
                      ? 'bg-[#0022B4] text-[#FFFFFF] border-[#0022B4]'
                      : song.collection?.toUpperCase() === 'CP'
                        ? 'bg-[#DA4A4A] text-[#FFFFFF] border-[#DA4A4A]'
                        : 'bg-blue-50 text-blue-700 border-blue-100'
                }`}
              >
                {song.collection}
              </span>
            )}
          </div>
          <button 
            onClick={onClose} 
            className="p-2 -mr-2 -mt-2 hover:bg-gray-100 rounded-full transition-colors text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
            aria-label="Close"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="overflow-y-auto p-6 space-y-8 bg-gray-50/50 flex-1">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12 space-y-4">
              <div className="animate-spin rounded-full h-10 w-10 border-4 border-blue-100 border-t-blue-600"></div>
              <p className="text-sm text-gray-400 font-medium animate-pulse">Fetching details...</p>
            </div>
          ) : (
            <>
              {/* Audio Section */}
              <section>
                <div className="flex items-center gap-2 mb-4">
                  <span className="bg-blue-100 text-blue-700 p-1.5 rounded-lg">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z" />
                    </svg>
                  </span>
                  <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Practice Tracks</h3>
                </div>
                
                <div className="grid grid-cols-1 gap-3">
                  {song?.media_files.filter(m => m.category === 'audio').map((file, idx) => (
                    <div 
                      key={idx} 
                      className="flex items-center justify-between p-4 bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md hover:border-blue-200 transition-all group"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-blue-50 rounded-full flex items-center justify-center text-blue-600 group-hover:bg-blue-600 group-hover:text-white transition-colors">
                          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M8 5v14l11-7z" />
                          </svg>
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900">{file.label}</p>
                          {file.voice_part && (
                            <span className="inline-block mt-1 text-[10px] font-bold bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full uppercase border border-blue-100">
                              {file.voice_part}
                            </span>
                          )}
                        </div>
                      </div>
                      <a 
                        href={file.url} 
                        target="_blank" 
                        rel="noreferrer"
                        className="px-4 py-2 bg-gray-50 text-gray-700 text-sm font-medium rounded-lg hover:bg-blue-600 hover:text-white transition-colors"
                      >
                        Play
                      </a>
                    </div>
                  ))}
                  {song?.media_files.filter(m => m.category === 'audio').length === 0 && (
                    <p className="text-sm text-gray-400 italic pl-2">No audio tracks available.</p>
                  )}
                </div>
              </section>

              {/* Score Section */}
              <section>
                 <div className="flex items-center gap-2 mb-4">
                  <span className="bg-red-100 text-red-700 p-1.5 rounded-lg">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 011.414.586l5.414 5.414a1 1 0 01.586 1.414V19a2 2 0 01-2 2z" />
                    </svg>
                  </span>
                  <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Sheet Music</h3>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {song?.media_files.filter(m => m.category === 'scores' || m.category === 'score').map((file, idx) => (
                    <a 
                      key={idx}
                      href={file.url}
                      target="_blank"
                      rel="noreferrer"
                      className="flex items-center gap-3 p-4 bg-white border border-gray-200 rounded-xl hover:border-red-300 hover:ring-2 hover:ring-red-50 hover:shadow-md transition-all group"
                    >
                      <div className="p-2 bg-red-50 text-red-500 rounded-lg group-hover:bg-red-500 group-hover:text-white transition-colors">
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 011.414.586l5.414 5.414a1 1 0 01.586 1.414V19a2 2 0 01-2 2z" />
                        </svg>
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className="block text-sm font-semibold text-gray-900 truncate group-hover:text-red-700 transition-colors">
                          {['HWC', 'VIP', 'CP'].includes(song?.collection?.toUpperCase()) 
                            ? `${song.title} (${song.collection} ${song.track_number})` 
                            : file.label}
                        </span>
                        <span className="text-xs text-gray-400">PDF Document</span>
                      </div>
                    </a>
                  ))}
                  {song?.media_files.filter(m => m.category === 'scores' || m.category === 'score').length === 0 && (
                    <p className="text-sm text-gray-400 italic pl-2 col-span-full">No sheet music available.</p>
                  )}
                </div>
              </section>
            </>
          )}
        </div>
        
        {/* Footer actions if needed, or just padding */}
        <div className="p-4 border-t bg-gray-50 flex justify-end">
          <button 
            onClick={onClose}
            className="px-5 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-100 transition shadow-sm text-sm"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default SongDetailModal;
